<script setup lang="ts">
import { SaleSteppedPrice } from '@lib/shared';
import type { PropType } from 'vue';

import { type NegotiationPlatformEntity, NegotiationState } from '~/apiClient';
import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

const isModalOpen = defineModel<boolean>({ required: true });

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { isMobile } = useResponsive();
const { negotiation, isBuyer, makeProposal, makeProposalIsPending, buyerStopProposals, buyerStopProposalsIsPending } = useAuctionNegotiation(
    () => auction
);

const getAmount = (base: number, deltaSteps: number): number => {
    const saleSteppedPrice = new SaleSteppedPrice(auction.minBidStepAmount, auction.minBidStepAmountThreshold, auction.maxBidStepAmount);

    return saleSteppedPrice.getDeltaStepsAmount(saleSteppedPrice.getStepRoundedReservePrice(base), deltaSteps);
};

// DATA
const buyerStopProposalsEnabled = ref(false);
const currentProposal = computed(() =>
    isBuyer.value ? negotiation.value.currentBuyerProposal.amount : negotiation.value.currentSellerProposal.amount
);
const selectedProposal = ref(currentProposal.value);
const minProposal = computed(() =>
    isBuyer.value ? getAmount(negotiation.value.currentBuyerProposal.amount, 1) : negotiation.value.currentBuyerProposal.amount
);
const maxProposal = computed(() =>
    isBuyer.value ? negotiation.value.currentSellerProposal.amount : getAmount(negotiation.value.currentSellerProposal.amount, -1)
);
const confirmButtonDisabled = computed(
    () =>
        selectedProposal.value > maxProposal.value ||
        (buyerStopProposalsEnabled.value && selectedProposal.value < currentProposal.value) ||
        (!buyerStopProposalsEnabled.value && selectedProposal.value < minProposal.value)
);
const resetProposalDisabled = computed(
    () => selectedProposal.value === (isBuyer.value ? negotiation.value.currentBuyerProposal.amount : negotiation.value.currentSellerProposal.amount)
);

const multiStepButtonsData = computed(() => {
    const absAllSteps = [2, 5, 10];
    const allSteps = isBuyer.value ? absAllSteps : absAllSteps.map(val => val * -1);

    return allSteps.map(steps => {
        const amount = getAmount(selectedProposal.value, steps);
        const delta = amount - selectedProposal.value;
        const labelSignPrefix = delta > 0 ? '+' : '';

        return {
            key: `steps_${steps}`,
            label: `${labelSignPrefix}${tn(delta, 'currency')}`,
            click: (): void => {
                selectedProposal.value = amount;
            },
            hidden: amount > maxProposal.value || amount < minProposal.value,
        };
    });
});

// METHODS
const closeModal = (): void => {
    isModalOpen.value = false;
};

const resetSelectedProposal = (): void => {
    selectedProposal.value = isBuyer.value ? negotiation.value.currentBuyerProposal.amount : negotiation.value.currentSellerProposal.amount;
};

const resetModal = (): void => {
    resetSelectedProposal();
    buyerStopProposalsEnabled.value = false;
};

const onUpdateProposal = (steps: number): void => {
    selectedProposal.value = Math.min(getAmount(selectedProposal.value, steps), negotiation.value.currentSellerProposal.amount);
};

const onMakeProposal = async (): Promise<void> => {
    try {
        let updatedNegotiation: NegotiationPlatformEntity | undefined;

        if (selectedProposal.value !== currentProposal.value) {
            updatedNegotiation = await makeProposal(selectedProposal.value);
        }

        const isNegotiationOpen = (updatedNegotiation ? updatedNegotiation.state : negotiation.value.state) === NegotiationState.Open;

        if (isBuyer.value && buyerStopProposalsEnabled.value && isNegotiationOpen) {
            await buyerStopProposals();
        }

        closeModal();
    } catch {}
};

// WATCHERS
watch(
    () => negotiation.value.state,
    value => isModalOpen.value && value !== NegotiationState.Open && closeModal()
);

watch(isModalOpen, (value, oldValue) => value && !oldValue && resetModal());
</script>

<template>
    <UModal
        v-if="auction"
        v-model:open="isModalOpen"
        :fullscreen="isMobile"
        :title="tt('auctionNegotiation.proposalModal.title')"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <div class="space-y-6 text-center">
                <i18n-t scope="global" tag="p" class="text-dimmed text-sm lg:text-base" keypath="auctionNegotiation.proposalModal.subtitle">
                    <template #countdown>
                        <BaseCountdown :expires-at="negotiation.expiresAt" class="text-primary-500" />
                    </template>
                </i18n-t>
                <div class="space-y-2">
                    <div class="grid grid-cols-2 gap-4">
                        <div class="lg:space-y-1">
                            <p class="text-dimmed text-xs lg:text-sm">
                                {{ tt(isBuyer ? 'auctionNegotiation.sellerRequest' : 'auctionNegotiation.yourRequest') }}
                            </p>
                            <p class="text-2xl font-semibold lg:text-3xl" :class="isBuyer ? 'text-dimmed' : 'text-primary-500'">
                                {{ tn(negotiation.currentSellerProposal.amount, 'currency') }}
                            </p>
                            <SaleFees
                                :reference-date="auction.startAt"
                                :is-buyer="isBuyer"
                                :base-amount="negotiation.currentSellerProposal.amount"
                                :assets-count="auction.assets.length"
                                :fee-configuration="auction.userMeta?.feeOverride"
                            />
                        </div>
                        <div class="lg:space-y-1">
                            <p class="text-dimmed text-xs lg:text-sm">
                                {{ tt(isBuyer ? 'auctionNegotiation.yourOffer' : 'auctionNegotiation.buyerOffer') }}
                            </p>
                            <p class="text-2xl font-semibold lg:text-3xl" :class="isBuyer ? 'text-primary-500' : 'text-dimmed'">
                                {{ tn(negotiation.currentBuyerProposal.amount, 'currency') }}
                            </p>
                            <SaleFees
                                :reference-date="auction.startAt"
                                :is-buyer="isBuyer"
                                :base-amount="negotiation.currentBuyerProposal.amount"
                                :assets-count="auction.assets.length"
                                :fee-configuration="auction.userMeta?.feeOverride"
                            />
                        </div>
                    </div>
                </div>

                <div class="bg-primary-50 space-y-4 rounded-xl p-4 lg:p-6">
                    <div>
                        <p class="text-xs font-extrabold tracking-widest uppercase lg:text-sm">
                            {{ tt(isBuyer ? 'auctionNegotiation.yourOffer' : 'auctionNegotiation.yourRequest') }}
                        </p>
                        <div class="mt-3 flex items-center justify-between">
                            <UButton
                                icon="lucide:minus"
                                size="lg"
                                color="neutral"
                                variant="outline"
                                class="disabled:bg-neutral-200 disabled:text-neutral-400"
                                square
                                :disabled="selectedProposal <= minProposal"
                                @click="onUpdateProposal(-1)"
                            />
                            <span class="text-primary text-2xl lg:text-3xl">{{ tn(selectedProposal, 'currency') }}</span>
                            <UButton
                                icon="lucide:plus"
                                size="lg"
                                color="neutral"
                                variant="outline"
                                class="disabled:bg-neutral-200 disabled:text-neutral-400"
                                square
                                :disabled="selectedProposal >= maxProposal"
                                @click="onUpdateProposal(1)"
                            />
                        </div>
                        <SaleFees
                            :reference-date="auction.startAt"
                            :is-buyer="isBuyer"
                            :base-amount="selectedProposal"
                            :assets-count="auction.assets.length"
                            :fee-configuration="auction.userMeta?.feeOverride"
                        />
                        <UButton
                            size="lg"
                            class="p-1"
                            variant="link"
                            :label="tt('auctionNegotiation.proposalModal.resetProposal')"
                            :disabled="resetProposalDisabled"
                            @click="resetSelectedProposal"
                        />
                    </div>

                    <div class="flex justify-center gap-3">
                        <UButton
                            v-for="buttonData in multiStepButtonsData"
                            :key="buttonData.key"
                            color="neutral"
                            variant="outline"
                            size="xl"
                            :label="buttonData.label"
                            :class="{ invisible: buttonData.hidden }"
                            @click="buttonData.click"
                        />
                    </div>

                    <USwitch v-if="isBuyer" v-model="buyerStopProposalsEnabled" color="success">
                        <template #label>
                            <p class="text-left text-sm lg:text-base">{{ tt('auctionNegotiation.proposalModal.stopProposalsLabel') }}</p>
                            <p v-if="buyerStopProposalsEnabled" class="text-dimmed text-left text-xs lg:text-sm">
                                {{ tt('auctionNegotiation.proposalModal.stopProposalsDescription') }}
                            </p>
                        </template>
                    </USwitch>
                </div>
            </div>
        </template>
        <template #footer="{ close }">
            <UButton
                size="xl"
                :label="tt(isBuyer ? 'auctionNegotiation.updateOffer' : 'auctionNegotiation.updateRequest')"
                block
                :loading="makeProposalIsPending || buyerStopProposalsIsPending"
                :disabled="confirmButtonDisabled"
                @click="onMakeProposal"
            />
            <UButton size="xl" block color="neutral" variant="link" :label="tt('auctionNegotiation.cancel')" @click="close" />
        </template>
    </UModal>
</template>
